import { config } from '../config'
import { requestStepUp, STEP_UP_REQUIRED } from '../lib/stepUp'

/**
 * Fælles HTTP-klient mod API-et.
 *
 * Tokenet hentes gennem en callback i stedet for at blive importeret direkte,
 * så både brugerpanelet (Entra ID access token) og TV-klienten (device token)
 * kan bruge den samme kode uden at kende til hinanden.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: string
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True når brugeren mangler rettigheder - UI viser en anden besked end ved fejl. */
  get isForbidden() {
    return this.status === 403
  }

  /** True når kaldet aldrig nåede frem (netværksfejl/offline). */
  get isOffline() {
    return this.status === 0
  }
}

type AuthHeaderProvider = () => Promise<Record<string, string>>

/** Standard: ingen autentificering. Sættes af AuthProvider og af TV-klienten. */
let authHeaderProvider: AuthHeaderProvider = async () => ({})

export function setAuthHeaderProvider(provider: AuthHeaderProvider) {
  authHeaderProvider = provider
}

/** Bruges af SignalR, der har brug for det rå token frem for færdige headers. */
export function getAuthHeaders() {
  return authHeaderProvider()
}

/**
 * Henter binært indhold (profilbilleder). Kan ikke gøres med et almindeligt
 * <img src>, fordi billedet kræver et Authorization-header.
 * Returnerer null hvis billedet ikke findes.
 */
export async function fetchBlob(path: string): Promise<Blob | null> {
  const headers = await authHeaderProvider()
  try {
    const response = await fetch(apiUrl(path), { headers })
    return response.ok ? await response.blob() : null
  } catch {
    return null
  }
}

/**
 * Sender en fil som multipart/form-data. Content-Type sættes af browseren (med boundary).
 * `fields` bliver til ekstra formfelter, f.eks. kategori og billedtekst på en sagsfil.
 *
 * Filen må være `null`: hurtigknappen i marken (beslutning 082) sender note og
 * foto i ét kald, og noten skal kunne stå alene. Serveren ser da et felt, der
 * ikke kom med - præcis som når et valgfrit tekstfelt er tomt.
 */
export async function uploadFile<T>(
  path: string,
  file: Blob | null,
  fileName: string,
  fields?: Record<string, string | undefined>
): Promise<T> {
  const form = new FormData()
  if (file) {
    form.append('file', file, fileName)
  }

  for (const [key, value] of Object.entries(fields ?? {})) {
    if (value !== undefined && value !== '') {
      form.append(key, value)
    }
  }

  const headers = await authHeaderProvider()
  let response: Response
  try {
    response = await fetch(apiUrl(path), { method: 'POST', headers, body: form })
  } catch {
    throw new ApiError('Kan ikke få forbindelse til serveren.', 0)
  }

  const text = await response.text()
  const payload = text ? safeParse(text) : undefined

  if (!response.ok) {
    const message =
      (payload as { message?: string } | undefined)?.message ?? `Serveren svarede med fejl ${response.status}.`
    throw new ApiError(message, response.status)
  }

  return payload as T
}

/**
 * Sender en hel formular (felter og flere filer) som multipart/form-data. Bruges, når billederne skal med i
 * samme kald som resten - fx en anonym nærved-hændelse (beslutning 113), der ikke kan findes igen bagefter.
 */
export async function postForm<T>(path: string, form: FormData, method: 'POST' | 'PUT' = 'POST'): Promise<T> {
  const headers = await authHeaderProvider()
  let response: Response
  try {
    response = await fetch(apiUrl(path), { method, headers, body: form })
  } catch {
    throw new ApiError('Kan ikke få forbindelse til serveren.', 0)
  }

  const text = await response.text()
  const payload = text ? safeParse(text) : undefined

  if (!response.ok) {
    const message =
      (payload as { message?: string } | undefined)?.message ?? `Serveren svarede med fejl ${response.status}.`
    throw new ApiError(message, response.status)
  }

  return payload as T
}

/**
 * Henter en fil fra API-et og lader browseren gemme den.
 *
 * Et almindeligt <a href> virker ikke: API-et kræver et Authorization-header,
 * som et link ikke sender. Filen hentes derfor som blob og gemmes derfra.
 * Filnavnet tages fra Content-Disposition, så serveren bestemmer det.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const headers = await authHeaderProvider()

  let response: Response
  try {
    response = await fetch(apiUrl(path), { headers })
  } catch {
    throw new ApiError('Kan ikke få forbindelse til serveren.', 0)
  }

  if (!response.ok) {
    const text = await response.text()
    const payload = text ? safeParse(text) : undefined
    throw new ApiError(
      (payload as { message?: string } | undefined)?.message ??
        `Filen kunne ikke hentes (fejl ${response.status}).`,
      response.status
    )
  }

  // Serveren sætter filnavnet i Content-Disposition; brug det hvis det er der.
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  const name = match ? decodeURIComponent(match[1]) : fallbackName

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = name
  document.body.appendChild(link)
  link.click()
  link.remove()

  // Giv browseren et øjeblik til at starte overførslen, før URL'en frigives.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

export function apiUrl(path: string) {
  return `${config.apiUrl}${path}`
}

async function request<T>(method: string, path: string, body?: unknown, stepUpToken?: string): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  Object.assign(headers, await authHeaderProvider())
  if (stepUpToken) {
    // Tokenet fra sikkerhedsnøglen (beslutning 092) - det i cachen har ikke kravet.
    headers.Authorization = `Bearer ${stepUpToken}`
  }

  let response: Response
  try {
    response = await fetch(apiUrl(path), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body)
    })
  } catch {
    throw new ApiError('Kan ikke få forbindelse til serveren.', 0)
  }

  // VPN kræver sikkerhedsnøgle (beslutning 092): bed om den, og prøv én gang til med det nye token.
  if (response.status === 401 && !stepUpToken) {
    const challenge = safeParse(await response.clone().text()) as { code?: string; claims?: string; message?: string } | undefined
    if (challenge?.code === STEP_UP_REQUIRED && challenge.claims) {
      const token = await requestStepUp(challenge.claims, challenge.message ?? 'Bekræft med din sikkerhedsnøgle.')
      if (!token) {
        throw new ApiError('Handlingen kræver din sikkerhedsnøgle (YubiKey) og blev ikke udført.', 401)
      }
      return request<T>(method, path, body, token)
    }
  }

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  const payload = text ? safeParse(text) : undefined

  if (!response.ok) {
    const message =
      (payload as { message?: string } | undefined)?.message ??
      (payload as { title?: string } | undefined)?.title ??
      `Serveren svarede med fejl ${response.status}.`
    throw new ApiError(message, response.status, (payload as { detail?: string } | undefined)?.detail)
  }

  return payload as T
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return { message: text }
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  delete: <T>(path: string) => request<T>('DELETE', path)
}
